import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import AuthLayout from '../layouts/AuthLayout';
import AppLayout from '../layouts/AppLayout';
import SuperAdminLayout from '../layouts/SuperAdminLayout';
import { RequireAuth } from '../features/auth/guards';
import { SuperAdminGuard } from '../features/auth/SuperAdminGuard';
import { SuspensionGuard } from '../features/auth/SuspensionGuard';
import { Navigate } from 'react-router-dom';

const HomePage = lazy(() => import('../pages/home/HomePage'));
const PublicProjectPage = lazy(() => import('../pages/public/PublicProjectPage'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('../pages/auth/RegisterPage'));
const AppDashboardPage = lazy(() => import('../pages/app/AppDashboardPage'));
const ProjectsListPage = lazy(() => import('../pages/app/ProjectsListPage'));
const ProjectCreatePage = lazy(() => import('../pages/app/ProjectCreatePage'));
const ProjectDetailPage = lazy(() => import('../pages/app/ProjectDetailPage'));
const DeliveryKitPage = lazy(() => import('../pages/app/DeliveryKitPage'));
const PaymentsPage = lazy(() => import('../pages/app/PaymentsPage'));
const SettingsPage = lazy(() => import('../pages/app/SettingsPage'));
const ClientProjectsPage = lazy(() => import('../pages/app/ClientProjectsPage'));
const SAOverviewPage = lazy(() => import('../pages/superadmin/SAOverviewPage'));
const SAUsersPage = lazy(() => import('../pages/superadmin/SAUsersPage'));
const SAPlansPage = lazy(() => import('../pages/superadmin/SAPlansPage'));
const SALeadsPage = lazy(() => import('../pages/superadmin/SALeadsPage'));
const SALandingPage = lazy(() => import('../pages/superadmin/SALandingPage'));
const SAMarketingEmailPage = lazy(() => import('../pages/superadmin/SAMarketingEmailPage'));
const SANotificationsPage = lazy(() => import('../pages/superadmin/SANotificationsPage'));
const SASettingsPage = lazy(() => import('../pages/superadmin/SASettingsPage'));
const SAProjectsPage = lazy(() => import('../pages/superadmin/SAProjectsPage'));
const SAPaymentsPage = lazy(() => import('../pages/superadmin/SAPaymentsPage'));
const SAInvoicesPage = lazy(() => import('../pages/superadmin/SAInvoicesPage'));
const PendingReviewPage = lazy(() => import('../pages/onboarding/PendingReviewPage'));
const SuspendedPage = lazy(() => import('../pages/account/SuspendedPage'));
const NotFound = lazy(() => import('../pages/NotFound'));
const SATaxRulesPage = lazy(() => import('../pages/superadmin/SATaxRulesPage'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    element: <PublicLayout />,
    children: [
      {
        path: '/p/:shareCode',
        element: <PublicProjectPage />,
      },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
    ],
  },
  {
    path: '/onboarding/pending-review',
    element: <PendingReviewPage />,
  },
  {
    path: '/account/suspended',
    element: <SuspendedPage />,
  },
  {
    element: (
      <RequireAuth>
        <SuspensionGuard>
          <AppLayout />
        </SuspensionGuard>
      </RequireAuth>
    ),
    children: [
      {
        path: '/app',
        element: <AppDashboardPage />,
      },
      {
        path: '/app/projects',
        element: <ProjectsListPage />,
      },
      {
        path: '/app/projects/new',
        element: <ProjectCreatePage />,
      },
      {
        path: '/app/projects/:id',
        element: <ProjectDetailPage />,
      },
      {
        path: '/app/projects/:id/delivery-kit',
        element: <DeliveryKitPage />,
      },
      {
        path: '/app/payments',
        element: <PaymentsPage />,
      },
      {
        path: '/app/settings',
        element: <SettingsPage />,
      },
      {
        path: '/app/client',
        element: <ClientProjectsPage />,
      },
    ],
  },
  {
    element: (
      <RequireAuth>
        <SuperAdminGuard>
          <SuperAdminLayout />
        </SuperAdminGuard>
      </RequireAuth>
    ),
    children: [
      { path: '/sa', element: <Navigate to="/sa/overview" replace /> },
      { path: '/sa/overview', element: <SAOverviewPage /> },
      { path: '/sa/users', element: <SAUsersPage /> },
      { path: '/sa/projects', element: <SAProjectsPage /> },
      { path: '/sa/payments', element: <SAPaymentsPage /> },
      { path: '/sa/invoices', element: <SAInvoicesPage /> },
      { path: '/sa/tax-rules', element: <SATaxRulesPage /> },
      { path: '/sa/plans', element: <SAPlansPage /> },
      { path: '/sa/leads', element: <SALeadsPage /> },
      { path: '/sa/landing', element: <SALandingPage /> },
      { path: '/sa/marketing/email', element: <SAMarketingEmailPage /> },
      { path: '/sa/marketing/notifications', element: <SANotificationsPage /> },
      { path: '/sa/settings', element: <SASettingsPage /> },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
